'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { Link, useRouter } from '@/i18n/navigation';
import { ImageOffIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { TableCell, TableRow } from '@/components/ui/table';
import LazyImage from '@/components/partials/lazy-image';
import NoImagePlaceholder from '@/components/partials/no-image-placeholder';
import PriceDisplay from '@/components/partials/price-display';
import AddToCartAction from '@/components/partials/card/add-to-cart-action';
import { getLocalizedValue, getResizedPictureUrl } from '@/lib/utils';
import { useAppLocale } from '@/hooks';
import { useLoginDialogStore } from '@/store';
import { ProductType } from '@/types/product';
type PropsType = {
  product: ProductType;
  detailHref: string;
  isAuthenticated: boolean;
};
const ProductItem = t0 => {
  const $ = _c(64);
  const {
    product,
    detailHref,
    isAuthenticated
  } = t0;
  const t = useTranslations();
  const router = useRouter();
  const {
    backendLocale
  } = useAppLocale();
  const {
    open: openLoginDialog
  } = useLoginDialogStore();
  let t1;
  if ($[0] !== product.mainPicture) {
    t1 = product.mainPicture ? getResizedPictureUrl(product.mainPicture.path, 160, 120) : null;
    $[0] = product.mainPicture;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const mainPictureUrl = t1;
  const isInStock = product.stock > 0;
  let t2;
  if ($[2] !== product.oemNumbers) {
    t2 = [...new Map(product.oemNumbers?.filter(_temp).map(_temp2)).values()];
    $[2] = product.oemNumbers;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const uniqueBrands = t2;
  let t3;
  if ($[4] !== product.oemNumbers) {
    t3 = [...new Map(product.oemNumbers?.filter(_temp3).map(_temp4)).values()];
    $[4] = product.oemNumbers;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const uniqueModels = t3;
  let t4;
  if ($[6] !== product.oemNumbers) {
    t4 = new Set(product.oemNumbers?.map(_temp5).filter(Boolean) ?? []);
    $[6] = product.oemNumbers;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = [...t4];
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const oemList = t5;
  const oemText = oemList.join(", ");
  let t6;
  if ($[10] !== backendLocale || $[11] !== product) {
    t6 = getLocalizedValue(product, "title", backendLocale);
    $[10] = backendLocale;
    $[11] = product;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const title = t6;
  let t7;
  if ($[13] !== detailHref || $[14] !== router) {
    t7 = () => router.push(detailHref);
    $[13] = detailHref;
    $[14] = router;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== mainPictureUrl || $[17] !== product.code) {
    t8 = mainPictureUrl ? <LazyImage src={mainPictureUrl} alt={product.code} fill={true} sizes="80px" className="object-contain p-1" /> : <div className="bg-muted flex h-full w-full items-center justify-center"><NoImagePlaceholder icon={ImageOffIcon} iconSize={5} /></div>;
    $[16] = mainPictureUrl;
    $[17] = product.code;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== detailHref || $[20] !== t8) {
    t9 = <TableCell className="w-px"><Link href={detailHref} className="relative block aspect-4/3 w-20 shrink-0 overflow-hidden rounded bg-gray-100">{t8}</Link></TableCell>;
    $[19] = detailHref;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== title) {
    t10 = <p className="line-clamp-2 text-sm font-semibold">{title}</p>;
    $[22] = title;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== detailHref || $[25] !== t10) {
    t11 = <TableCell className="whitespace-normal"><Link href={detailHref}>{t10}</Link></TableCell>;
    $[24] = detailHref;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== product.code) {
    t12 = <TableCell className="text-sm">{product.code}</TableCell>;
    $[27] = product.code;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== oemList.length || $[30] !== oemText) {
    t13 = <TableCell className="whitespace-normal">{oemList.length > 0 ? <span className="text-muted-foreground line-clamp-2 text-xs" title={oemText}>{oemText}</span> : <span className="text-muted-foreground/40 text-xs">—</span>}</TableCell>;
    $[29] = oemList.length;
    $[30] = oemText;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== uniqueBrands) {
    t14 = <TableCell className="whitespace-normal">{uniqueBrands.length > 0 ? <span className="text-muted-foreground line-clamp-2 text-xs" title={uniqueBrands.join(", ")}>{uniqueBrands.slice(0, 4).join(", ")}{uniqueBrands.length > 4 ? "\u2026" : ""}</span> : <span className="text-muted-foreground/40 text-xs">—</span>}</TableCell>;
    $[32] = uniqueBrands;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== uniqueModels) {
    t15 = <TableCell className="whitespace-normal">{uniqueModels.length > 0 ? <span className="text-muted-foreground line-clamp-2 text-xs" title={uniqueModels.join(", ")}>{uniqueModels.slice(0, 4).join(", ")}{uniqueModels.length > 4 ? "\u2026" : ""}</span> : <span className="text-muted-foreground/40 text-xs">—</span>}</TableCell>;
    $[34] = uniqueModels;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const t16 = isInStock ? "outline" : "destructive";
  let t17;
  if ($[36] !== isInStock || $[37] !== t) {
    t17 = isInStock ? t("in_stock") : t("out_of_stock");
    $[36] = isInStock;
    $[37] = t;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t16 || $[40] !== t17) {
    t18 = <TableCell><Badge variant={t16} className="text-xs">{t17}</Badge></TableCell>;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== backendLocale || $[43] !== isAuthenticated || $[44] !== openLoginDialog || $[45] !== product.price?.currencyCode || $[46] !== product.price?.discountRate || $[47] !== product.price?.gross || $[48] !== product.price?.originalGross || $[49] !== t) {
    t19 = <TableCell className="text-right">{isAuthenticated ? <PriceDisplay gross={product.price?.gross} originalGross={product.price?.originalGross} discountRate={product.price?.discountRate} currencyCode={product.price?.currencyCode} locale={backendLocale} size="sm" /> : <button onClick={e => {
        e.stopPropagation();
        openLoginDialog();
      }} className="text-muted-foreground hover:text-brand border-muted-foreground hover:border-brand cursor-pointer rounded border px-2 py-0.5 text-xs transition-colors">{t("login_to_see_price")}</button>}</TableCell>;
    $[42] = backendLocale;
    $[43] = isAuthenticated;
    $[44] = openLoginDialog;
    $[45] = product.price?.currencyCode;
    $[46] = product.price?.discountRate;
    $[47] = product.price?.gross;
    $[48] = product.price?.originalGross;
    $[49] = t;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== product) {
    t20 = <TableCell onClick={_temp6}><div className="flex justify-end"><AddToCartAction product={product} size="icon" /></div></TableCell>;
    $[51] = product;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== t11 || $[54] !== t12 || $[55] !== t13 || $[56] !== t14 || $[57] !== t15 || $[58] !== t18 || $[59] !== t19 || $[60] !== t20 || $[61] !== t7 || $[62] !== t9) {
    t21 = <TableRow className="cursor-pointer transition-colors duration-200" onClick={t7}>{t9}{t11}{t12}{t13}{t14}{t15}{t18}{t19}{t20}</TableRow>;
    $[53] = t11;
    $[54] = t12;
    $[55] = t13;
    $[56] = t14;
    $[57] = t15;
    $[58] = t18;
    $[59] = t19;
    $[60] = t20;
    $[61] = t7;
    $[62] = t9;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  return t21;
};
export default ProductItem;
function _temp(oem) {
  return oem.model?.brand;
}
function _temp2(oem_0) {
  return [oem_0.model.brand.id, oem_0.model.brand.name];
}
function _temp3(oem_1) {
  return oem_1.model;
}
function _temp4(oem_2) {
  return [oem_2.model.id, oem_2.model.name];
}
function _temp5(oem_3) {
  return oem_3.oem;
}
function _temp6(e_0) {
  return e_0.stopPropagation();
}